import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { FiAlertCircle, FiX, FiUpload, FiSend, FiCheckCircle, FiMessageSquare } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { submitIssueReport } from '../../firebase';
import { getErrorLog } from '../../errorLogger';



const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE = 50 * 1024 * 1024;

export default function FeedbackButton() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [description, setDescription] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (user) return;
        setOpen(false);
        setDescription('');
        setSubmitted(false);
        setSubmitting(false);
        setDragOver(false);
        setAttachments(prev => {
            prev.forEach(a => URL.revokeObjectURL(a.preview));
            return [];
        });
    }, [user]);

    const addAttachment = (file) => {
        if (!file) return;
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        if (!isImage && !isVideo) return;
        if (file.size > MAX_FILE_SIZE) {
            alert(`"${file.name}" exceeds the 50 MB limit and was not added.`);
            return;
        }
        setAttachments(prev => {
            if (prev.length >= MAX_ATTACHMENTS) return prev;
            return [...prev, { file, preview: URL.createObjectURL(file), isVideo }];
        });
    };

    useEffect(() => {
        if (!open) return;
        const handlePaste = (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;
            for (const item of items) {
                if (item.type.startsWith('image/')) addAttachment(item.getAsFile());
            }
        };
        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [open]);

    const handleClose = () => {
        if (submitting) return;
        attachments.forEach(a => URL.revokeObjectURL(a.preview));
        setOpen(false);
        setDescription('');
        setAttachments([]);
        setSubmitted(false);
    };

    const removeAttachment = (index) => {
        setAttachments(prev => {
            URL.revokeObjectURL(prev[index].preview);
            return prev.filter((_, i) => i !== index);
        });
    };

    const handleFiles = (files) => Array.from(files).forEach(addAttachment);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
    };

    const handleSubmit = async () => {
        if (!description.trim() || submitting) return;
        setSubmitting(true);
        try {
            await submitIssueReport({
                description: description.trim(),
                pageUrl: window.location.href,
                userEmail: user?.email || 'anonymous',
                userId: user?.uid || null,
                attachments: attachments.map(a => a.file),
                errorLog: getErrorLog(),
            });
            setSubmitted(true);
        } catch (err) {
            console.error('Failed to submit issue report:', err);
        } finally {
            setSubmitting(false);
        }
    };

    if (!user) {
        return null;
    }

    return (
        <>
            <FloatingBtn onClick={() => setOpen(true)}>
                <FiMessageSquare size={15} />
                Feedback
            </FloatingBtn>

            {open && ReactDOM.createPortal(
                <Overlay onClick={handleClose}>
                    <Dialog onClick={e => e.stopPropagation()}>
                        {submitted ? (
                            <SuccessView>
                                <FiCheckCircle size={48} color="#9333ea" />
                                <SuccessTitle>Thanks for your feedback!</SuccessTitle>
                                <SuccessMsg>We've received your report and will look into it soon.</SuccessMsg>
                                <PrimaryBtn onClick={handleClose}>Close</PrimaryBtn>
                            </SuccessView>
                        ) : (
                            <>
                                <Header>
                                    <HeaderTitle>
                                        <FiAlertCircle size={18} />
                                        Report an Issue
                                    </HeaderTitle>
                                    <CloseIcon onClick={handleClose}>
                                        <FiX size={20} />
                                    </CloseIcon>
                                </Header>

                                <Body>
                                    <Label>Description <Required>*</Required></Label>
                                    <Textarea
                                        placeholder="Describe the issue and steps to reproduce it..."
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        rows={4}
                                    />

                                    <Label>
                                        Attachments <Required>*</Required> (up to {MAX_ATTACHMENTS} · images &amp; videos, max 50 MB each)
                                    </Label>
                                    <UploadArea
                                        $dragOver={dragOver}
                                        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={handleDrop}
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <FiUpload size={20} color="#9333ea" />
                                        <UploadText>Click to upload or drag &amp; drop · images and videos</UploadText>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*,video/*"
                                            multiple
                                            hidden
                                            onChange={e => { handleFiles(e.target.files); e.target.value = ''; }}
                                        />
                                    </UploadArea>

                                    {attachments.length > 0 && (
                                        <Previews>
                                            {attachments.map((a, i) => (
                                                <PreviewItem key={i}>
                                                    {a.isVideo
                                                        ? <PreviewVideo src={a.preview} />
                                                        : <PreviewImg src={a.preview} alt={`Attachment ${i + 1}`} />
                                                    }
                                                    <RemoveBtn onClick={() => removeAttachment(i)}>
                                                        <FiX size={12} />
                                                    </RemoveBtn>
                                                </PreviewItem>
                                            ))}
                                        </Previews>
                                    )}

                                    <MetaInfo>Page: {window.location.pathname}</MetaInfo>
                                </Body>

                                <Footer>
                                    <CancelBtn onClick={handleClose} disabled={submitting}>Cancel</CancelBtn>
                                    <PrimaryBtn
                                        onClick={handleSubmit}
                                        disabled={!description.trim() || attachments.length === 0 || submitting}
                                    >
                                        {submitting
                                            ? 'Submitting...'
                                            : <><FiSend size={14} style={{ marginRight: 6 }} />Submit</>
                                        }
                                    </PrimaryBtn>
                                </Footer>
                            </>
                        )}
                    </Dialog>
                </Overlay>,
                document.body
            )}
        </>
    );
}

const FloatingBtn = styled.button`
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 10000;
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 10px 18px;
    border-radius: 9999px;
    background: #ffffff;
    color: #1f2937;
    border: 1px solid #e5e7eb;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    font-family: inherit;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    transition: background 0.2s, border-color 0.2s, box-shadow 0.2s, transform 0.1s;

    &:hover {
        background: #f9fafb;
        border-color: #d1d5db;
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.13);
    }
    &:active {
        transform: scale(0.95);
    }
`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10001;
`;

const Dialog = styled.div`
    background: white;
    border-radius: 1.5rem;
    width: 90%;
    max-width: 520px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #f3f4f6;
    flex-shrink: 0;
`;

const HeaderTitle = styled.h3`
    font-size: 1.1rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CloseIcon = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: #9ca3af;
    display: flex;
    align-items: center;
    padding: 4px;
    border-radius: 6px;
    transition: all 0.15s;

    &:hover {
        color: #374151;
        background: #f3f4f6;
    }
`;

const Body = styled.div`
    padding: 1.25rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    overflow-y: auto;
`;

const Label = styled.label`
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    display: block;
`;

const Required = styled.span`
    color: #ef4444;
`;

const Textarea = styled.textarea`
    width: 100%;
    border: 1.5px solid #e5e7eb;
    border-radius: 0.75rem;
    padding: 0.75rem;
    font-size: 0.9rem;
    color: #111827;
    resize: vertical;
    font-family: inherit;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s;

    &:focus {
        border-color: #9333ea;
        box-shadow: 0 0 0 3px rgba(147, 51, 234, 0.1);
    }
`;

const UploadArea = styled.div`
    border: 2px dashed ${({ $dragOver }) => $dragOver ? '#9333ea' : '#d1d5db'};
    border-radius: 0.75rem;
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.15s;
    background: ${({ $dragOver }) => $dragOver ? 'rgba(147, 51, 234, 0.05)' : '#fafafa'};

    &:hover {
        border-color: #9333ea;
        background: rgba(147, 51, 234, 0.04);
    }
`;

const UploadText = styled.p`
    font-size: 0.85rem;
    color: #6b7280;
    margin: 0;
    text-align: center;
`;

const Previews = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const PreviewItem = styled.div`
    position: relative;
    width: 72px;
    height: 72px;
    border-radius: 0.5rem;
    overflow: hidden;
    border: 1.5px solid #e5e7eb;
`;

const PreviewImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const PreviewVideo = styled.video`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const RemoveBtn = styled.button`
    position: absolute;
    top: 2px;
    right: 2px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &:hover {
        background: rgba(0, 0, 0, 0.85);
    }
`;

const MetaInfo = styled.p`
    font-size: 0.75rem;
    color: #9ca3af;
    margin: 0;
`;

const Footer = styled.div`
    display: flex;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid #f3f4f6;
    flex-shrink: 0;
`;

const CancelBtn = styled.button`
    flex: 1;
    padding: 11px 22px;
    border-radius: 9999px;
    background: #f3f4f6;
    color: #374151;
    font-weight: 600;
    border: none;
    cursor: pointer;
    font-size: 0.9rem;
    transition: background 0.15s, transform 0.1s;

    &:hover:not(:disabled) {
        background: #e5e7eb;
    }
    &:active:not(:disabled) {
        transform: scale(0.95);
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const PrimaryBtn = styled.button`
    flex: 1;
    padding: 11px 22px;
    border-radius: 9999px;
    background: #9333ea;
    color: white;
    font-weight: 600;
    border: none;
    cursor: pointer;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: background 0.15s, transform 0.1s;

    &:hover:not(:disabled) {
        background: #7c3aed;
    }
    &:active:not(:disabled) {
        transform: scale(0.95);
    }
    &:focus-visible {
        outline: 2px solid #9333ea;
        outline-offset: 2px;
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const SuccessView = styled.div`
    padding: 3rem 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    text-align: center;
`;

const SuccessTitle = styled.h3`
    font-size: 1.3rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
`;

const SuccessMsg = styled.p`
    color: #6b7280;
    margin: 0;
    font-size: 0.95rem;
`;
